<template>
  <section class="section-development-banner">
    <div class="__development-banner-wrapper">
      <!-- Image -->
      <div class="__development-image-wrapper" @click="isGalleryOpen = true;">
        <div class="__primary-image __column" :style="`background-image: url('${development.bannerImageUrl}')`" />
      </div>

      <!-- Go back button -->
      <div class="__go-back-button-wrapper">
        <div class="container">
          <ElementsAvatar class="__go-back-btn" :icon-src="chevronDownIcon" icon-alt="Parking icon" @click="goBack()" />
          <ElementsAvatar :icon-src="galleryIcon" icon-alt="Parking icon" is-clickable @click="isGalleryOpen = true;" />
        </div>
      </div>

      <!-- Banner content -->
      <div class="__banner-content-wrapper">
        <div class="container">
          <!-- Title -->
          <h1 class="__development-title">
            {{ development.name }}
          </h1>

          <!-- Development meta data -->
          <div class="__info-action-bar">
            <div class="__info-bar">
              <div class="__development-type">
                {{ development.type.toUpperCase() }}
              </div>
              <div class="__development-views">
                <img v-lazy-load  :data-src="eyeIcon" alt="View icon">
                Views: <BlocksLoadingDots v-if="viewsCount === null" /> <span v-else style="margin-left: 2px">{{ numberFormat(viewsCount) }}</span>
              </div>
              <div class="__development-added-date">
                <img v-lazy-load  :data-src="calendarIcon" alt="calendar icon">
                Added on: {{ formatDate(development.created_at) }}
              </div>
            </div>

            <div class="__action-bar-and-video-button-wrapper">
              <!-- Video and 360 section -->
              <div v-if="development.video360Url || development.videoUrl" class="__video-button-wrapper">
                <ElementsButtonsIcon v-if="development.videoUrl" :icon-src="videoIcon" label="Video" @click="playVideo('video')" />
                <div v-if="development.videoUrl && development.video360Url" class="__separator" />
                <ElementsButtonsIcon v-if="development.video360Url" :icon-src="video360Icon" label="360 Tour" @click="playVideo('360')" />
              </div>

              <!-- Action bar -->
              <div class="__action-bar">
                <!-- Favorite button -->
                <div class="__favorite-button-wrapper" :class="{'--is-favorite': isAFavorite}">
                  <ElementsAvatar :icon-src="isAFavorite ? favoriteSelectedIcon : favoriteIcon" icon-alt="Favorite icon" @click="toggleAddToFavorite();" />
                </div>
                <CommonSocialSharePlatforms :url="shareUrl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Gallery Popup -->
    <BlocksGalleryFullScreen v-model="isGalleryOpen" :selected-image-index="selectedImageIndex" :images="galleryMedias" :analytics-event-data="{entity: 'Development', slug: development.slug, displayedLocation: 'development view', imageType: 'gallery'}" />
  </section>
</template>

<script setup lang="ts">
import eyeIcon from '~/assets/images/icons/eye.webp';
import calendarIcon from '~/assets/images/icons/calendar.webp';
import favoriteIcon from '~/assets/images/icons/favorite.webp';
import chevronDownIcon from '~/assets/images/icons/chevron-down.webp';
import galleryIcon from '~/assets/images/icons/gallery.webp';
import {computed, ComputedRef, Ref} from 'vue';
import {Development} from '~/@types/development';
import formatDate from '../../../composables/formatDate';
import favoriteSelectedIcon from 'assets/images/icons/favorite-selected.webp';
import {useAuthUserStore} from '~/store/authUser';
import globalApiHeaders from '~/composables/globalApiHeaders';
import numberFormat from '~/composables/numberFormat';
import videoIcon from 'assets/images/icons/video.webp';
import video360Icon from 'assets/images/icons/360-video.webp';
import {ImageItem} from '~/@types/common';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  development: {
    type: Object as PropType<Development>,
    required: true,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const isGalleryOpen: Ref<boolean> = ref(false);
const selectedImageIndex: Ref<number> = ref(0);
const router = useRouter();
const shareUrl: Ref<string> = ref('');
const authUserStore = useAuthUserStore();
const config = useRuntimeConfig();
const viewsCount: Ref<number | null> = ref(null);
const {$metaPixelTrackEvent} = useNuxtApp();

onMounted(() => {
  shareUrl.value = window && window.location ? window.location.href : '';

  // Get view count
  getViewsCount();
});

/**
 * Play video
 *
 * @param type
 */
function playVideo (type: 'video' | '360') {
  if (type === 'video' && props.development?.videoUrl) {
    selectedImageIndex.value = 2;
    isGalleryOpen.value = true;
  } else if (props.development?.video360Url) {
    selectedImageIndex.value = props.development.videoUrl ? 3 : 2;
    isGalleryOpen.value = true;
  }
}

/**
 * Get gallery medias
 */
const galleryMedias: ComputedRef<ImageItem[]> = computed(() => {
  if (props.development) {
    const returnArray = props.development.gallery.data.map((image) => ({
      url: image.url,
      type: 'image',
      caption: image.caption || '',
    } as ImageItem));

    const findHeroImage = props.development?.gallery.data.find((image) => image.url === props.development?.heroImageUrl);
    if (!findHeroImage && props.development?.heroImageUrl) {
      returnArray.unshift({
        url: props.development.heroImageUrl,
        type: 'image',
        caption: '',
      });
    }

    // Array to hold videos to be inserted
    const videosToInsert = [];

    if (props.development.videoUrl) {
      videosToInsert.push({
        url: props.development.videoUrl,
        type: 'video',
        caption: `${props.development.name} video`,
      });
    }

    // Insert videos at the second and third positions
    returnArray.splice(1, 0, ...videosToInsert);

    return returnArray;
  }
  return [];
});

/**
 * Add/Remove from favorite
 */
function toggleAddToFavorite () {
  const favorites = authUserStore.favorites;
  const did = props.development.did;
  if (favorites && favorites.length > 0) {
    const index = favorites.findIndex((item) => item.did === did);
    if (index > -1) {
      authUserStore.setFavorites(favorites.filter((item) => item.did !== did));
    } else {
      authUserStore.setFavorites([...favorites, {
        type: 'development',
        did: did,
      }]);
    }

    // Add to favorites Pixel event
    $metaPixelTrackEvent('AddToWishlist', {
      content_name: props.development.name,
      content_category: props.development.type ?? 'Development',
      content_ids: [props.development.did],
      content_type: 'Development',
    });
  } else {
    authUserStore.setFavorites([{
      type: 'development',
      did: did,
    }]);
  }
}

/**
 * Check whether this listing is a favorite or not
 */
const isAFavorite: ComputedRef<boolean> = computed(() => {
  const favorites = authUserStore.favorites;
  const did = props.development.did;
  if (favorites && favorites.length > 0) {
    const index = favorites.findIndex((item) => item.did === did);
    return index > -1;
  }
  return false;
});

function goBack () {
  router.back();
}

/**
 * Get views count
 */
function getViewsCount () {
  setTimeout(() => {
    if (props.development?.slug) {
      $fetch(`/public/developments/view-count/${props.development.slug}`,
        { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
      ).then((response) => {
        viewsCount.value = response as number;
      });
    }
  }, 1500);
}
</script>

<style lang="scss">
.section-development-banner {
  padding-top: 0;
  height: 520px;
  padding-bottom: 38px;

  .__development-banner-wrapper {
    position: relative;
    height: 100%;

    .__development-image-wrapper {
      height: 100%;
      display: flex;
      cursor: pointer;

      .__column {
        height: 100%;
        width: 100%;
      }

      .__primary-image {
        background-size: cover;
        background-position: center;
      }
    }

    .__go-back-button-wrapper {
      position: absolute;
      top: 20px;
      width: 100%;
      height: fit-content;

      .container {
        display: flex;
        justify-content: space-between;
      }

      .__go-back-btn img {
        transform: rotate(90deg);
      }
    }

    .__banner-content-wrapper {
      padding: 120px 0 0;
      position: absolute;
      bottom: 0;
      width: 100%;
      height: fit-content;
      color: rgb(var(--text-light));
      z-index: 1;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #272655 100%);

      .__development-title {
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.25);
        font-size: 36px;
        font-weight: 500;
      }

      .__info-action-bar {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        justify-content: space-between;
        padding: 8px 0 5px;

        .__info-bar {
          display: flex;
          align-items: center;
          font-weight: 300;
          font-size: 14px;

          &>:not(:last-child) {
            margin-right: 30px;
          }

          .__development-type {
            text-transform: uppercase;
            font-size: 15px;
          }

          &>* {
            display: flex;
            align-items: center;
          }

          .__development-views {
            img {
              width: 18px;
              height: auto;
              margin-right: 8px;
            }
          }

          .__development-added-date {
            img {
              width: 14px;
              height: auto;
              margin-right: 8px;
            }
          }
        }
        .__action-bar-and-video-button-wrapper {
          display: flex;
        }

        .__video-button-wrapper {
          margin-right: 24px;
          display: flex;
          padding: 4px 20px;
          border-radius: 20px;
          background: rgb(var(--background-light), 0.3);
          box-shadow: 0px -3px 4px rgba(0, 0, 0, 0.25);
          backdrop-filter: blur(2px);

          .__separator {
            width: 1px;
            height: 100%;
            background: #b2b2b2;
            margin-left: 12px;
            margin-right: 12px;
          }

          .el-icon-button {
            width: 80px;
            display: flex;
            justify-content: center;

            .__icon-button-label {
              color: #fff;
            }

            .__el-icon-button-inner {
              border-radius: 50%;
              padding: 8px;
              background: #fff;
              display: flex;
              justify-content: center;
              align-items: center;
            }

            &:hover {
              background: unset !important;
            }
          }

          @media screen and (max-width: $grid-md) {
            display: none;
          }
        }

        .__action-bar {
          display: flex;
          padding: 4px 5px;
          border-radius: 20px;
          background: rgb(var(--background-light), 0.3);
          box-shadow: 0px -3px 4px rgba(0, 0, 0, 0.25);
          backdrop-filter: blur(2px);

          :not(:first-child) {
            border-left: 1px solid rgba(var(--text-light), 0.5);
          }

          .el-avatar {
            padding: 0 12px;
            align-items: center;
            display: flex;
          }

          .__favorite-button-wrapper {
            margin-right: 8px;
            cursor: pointer;
            transition: all 0.4s ease-in-out !important;

            .el-avatar .__el-avatar-inner {
              scale: 1;
              transition: all 0.2s ease-in-out !important;
            }

            &.--is-favorite {
              .el-avatar {
                .__el-avatar-inner {
                  -webkit-box-shadow: inset 0 0 8px 0 rgba(0,0,0,0.4) !important;
                  box-shadow: inset 0 0 5px 0 rgba(0,0,0,0.4) !important;
                  transition: all 0.4s ease-in-out !important;
                }
              }
            }

            &:hover {
              .el-avatar {
                .__el-avatar-inner {
                  scale: 1.1;
                  transition: all 0.2s ease-in-out !important;
                }
              }
            }
          }
        }
      }

      // Responsive
      @media screen and (max-width: $grid-lg) {
        .__development-title {
          text-align: center;
          width: 100%;
          margin-bottom: 8px;
        }

        .__info-action-bar {
          justify-content: center;
          .__info-bar {
            margin-bottom: 12px;
          }
        }
      }

      @media screen and (max-width: $grid-sm) {
        .__development-title {
          font-size: 28px;
        }

        .__info-action-bar {
          padding-bottom: 0;

          .__info-bar {
            font-size: 12px;

            &>:not(:last-child) {
              margin-right: 15px;
            }

            .__development-type {
              font-size: 14px;
            }
          }

          .__action-bar {
            padding: 2px 4px;
            border-radius: 20px;
            margin-right: 0;

            .el-icon-button {

              &:not(:last-child) {
                margin-right: 12px;
              }

              .__el-icon-button-inner {
                width: 20px !important;
                padding: 4px !important;

                img {
                  width: 100%;
                  height: auto;
                }
              }

              .__label {
                font-size: 12px !important;
              }
            }

            .el-avatar {
              padding: 0 5px;

              .__el-avatar-inner {
                width: 25px;
                height: 25px;
              }
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: $grid-md) {
    height: 420px;

    .__development-banner-wrapper .__development-image-wrapper {
      flex-direction: column;
    }
  }
}
</style>
